import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import Utils from "../../shared/utils";
import * as moment from "moment";
import { LocationViewModel, CountryDataModel, LocationDataModel, LocationOpenDay, LocationOpenTime, BonusLocationViewModel, MenuLocationViewModel, MenuChangeLocationViewModel, LocationOpenSpecialDay, LocationWebshopViewModel, EventLocationViewModel, BasketLocationViewModel, LocationDetailViewModel, CheckoutLocationViewModel, LocationOpenDayViewModel, CheckTimeslotOrderAmount } from "../../shared/models/location.model";
import { LikeService } from "../../services/like.service";
import { BasketItemViewModel } from "../../shared/models/webshop.model";
import { ContentType, CustomAction, ImageOrientation, LocationServiceStatus, orderTypes, WebShopMode } from "../../shared/contentTypes";
import { CoreLogService } from "../../services/log.service";
import { environment } from "../../../../environments/environment";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ReservationSettingService } from "../../services/reservationSetting.service";
import { flattenStyles } from "@angular/platform-browser/src/dom/dom_renderer";
import { Coordinates } from "../../services/com/comEnvironmentGeolocationCoordinates";
import { CoreOpenDayTime } from "../../languages/openDayTime";
import { OrderType } from "../../services/IInHouseOrderService";
import { DeviceLocationService } from "../../services/deviceLocation.service";

@Injectable({
  providedIn: "root"
})
export class LocationService implements OnDestroy {
  private _locationObsServ: IObjectObserver<LocationDataModel>;
  private _locationSupscription: Subscription;
  // private _countryObsServ: ObjectObserver<CountryDataModel>;
  // private _countrySupscription: Subscription;
  /** Subject to easy observe changes of location or country */
  private _dataChange: Subject<(string | number)[]>;

  constructor(private _likeService: LikeService,
    private _reservationSettingService: ReservationSettingService,
    private _coreLogService: CoreLogService,
    private objObsInjectHelper: CircularInjectionHelper,
    private _deviceLocationService: DeviceLocationService) {
    this._locationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LocationDataModel>(ContentType.Location, objObsInjectHelper);
    // this._countryObsServ = this._objObsServ.GetObjectObserver<CountryDataModel>(ContentType.);

    this._dataChange = new Subject<(string | number)[]>();

    this._locationSupscription = this._locationObsServ.ObserveChanges.subscribe((keys) => {
      this._dataChange.next(keys);
    });

    // this._countrySupscription = this._countryObsServ.ObserveChanges.subscribe((country_keys) => {
    //   // todo search for location with using the country_keys
    //   // this._dataChange.next(location_keys);
    // });
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._dataChange.asObservable();
  }

  /**
  * Getting the list of locationDataModel
  * Create locationViewModelList
  * @param useCurrentLocation whether to query for the currentLocation to calculate distance. 
  *    Set to false to avoid unneccesarily asking for Permission
  * @param hasClockLabel show "o'clock" or "Uhr" label in open time z.B 10:30 22:00 Uhr
  * @result return a promise that resolves a list of locationViewModel
  */
  public GetLocationViewModel(useCurrentLocation: boolean = true, hasClockLabel: boolean = false, locationServiceStatus: LocationServiceStatus = LocationServiceStatus.IsNotActivated): Promise<LocationViewModel[]> {
    const me = this;
    // return me.getLocationViewModel();
    return new Promise<LocationViewModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && model.visible)
        .then(async (locations) => {
          // TODO muss das Datum und Zeitzone des Standorts betrachtet werden?
          const now = moment();
          const currentTimeYMD = Utils.GetYMDFromMoment(now);
          const currentTimeHMS = Utils.GetHMSFromMoment(now);

          let coordinates = null;
          if (locationServiceStatus === LocationServiceStatus.Authorized && useCurrentLocation) {
            coordinates = await me._deviceLocationService.getCurrentPosition();
          }
          me.mapDataModelListToViewModelList(locations, currentTimeYMD, currentTimeHMS, coordinates, hasClockLabel)
            .then(resolve)
            .catch(e => {
              me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
              reject();
            });
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get the list of location for each bonus action
   * @param locationList locationList to filter location
   */
  public GetBunosActionLocation(locationList: number[]): Promise<BonusLocationViewModel[]> {
    const me = this;
    return new Promise<BonusLocationViewModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && locationList.includes(model.hyp_cus_nr))
        .then(response => {
          resolve(me.mapModelListToBonusLocationViewModelList(response));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetBunosActionLocation", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
 * get the list of location for each bonus action
 * @param locationList locationList to filter location
 */
  public GetEventActionLocations(locationList: number[]): Promise<EventLocationViewModel[]> {
    const me = this;
    return new Promise<EventLocationViewModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && locationList.includes(model.hyp_cus_nr))
        .then(response => {
          resolve(me.mapModelListToEventLocationViewModelList(response));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetEventActionLocation", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get the black list of location for each bonus action
   * @param locationList locationList to filter location
   */
  public GetBlackListBunosActionLocation(locationList: number[]): Promise<BonusLocationViewModel[]> {
    const me = this;
    return new Promise<BonusLocationViewModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && !locationList.includes(model.hyp_cus_nr))
        .then(response => {
          resolve(me.mapModelListToBonusLocationViewModelList(response));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetBunosActionLocation", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get the location of menu
   * @param customerNumber customerNumber to filter location
   */
  public GetMenuLocation(customerNumber: number): Promise<MenuLocationViewModel> {
    const me = this;
    return new Promise<MenuLocationViewModel>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && model.hyp_cus_nr === customerNumber)
        .then(response => {
          resolve(me.mapModelToMenuLocationViewModel(response[0]));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetMenuLocation", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get the location of for menuChangeLocationDialog
   * @param customerNumber customerNumber to filter location
   */
  public GetMenuChangeLocation(customerNumber: number, serviceID: number): Promise<MenuChangeLocationViewModel> {
    const me = this;
    return new Promise<MenuChangeLocationViewModel>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && model.hyp_cus_nr === customerNumber)
        .then(response => {
          resolve(me.mapModelToMenuChangeLocationViewModel(response[0], serviceID));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetMenuLocation", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * Filtert die Locations mit unterstützten ServiceMode.
   */
  private filterLocationByServiceMode(locations: LocationDataModel[], serviceMode: number): LocationDataModel[] {
    const locationList: LocationDataModel[] = [];
    if (serviceMode === WebShopMode.Delivery) {
      locationList.push(...locations.filter(f => f.service_delivery));
    } else if (serviceMode === WebShopMode.TakeAway) {
      locationList.push(...locations.filter(f => f.service_pickup));
    } else if (serviceMode === WebShopMode.PickupGroup) {
      locationList.push(...locations.filter(f => f.service_pickup_group));
    } else if (serviceMode === WebShopMode.DeliveryGroup) {
      locationList.push(...locations.filter(f => f.service_delivery_group));
    } else if (serviceMode === WebShopMode.OrderAhead) {
      locationList.push(...locations.filter(f => f.service_orderahead));
    } else if (serviceMode === WebShopMode.OrderToTable) {
      locationList.push(...locations.filter(f => f.service_inhouse));
    } else if (serviceMode === WebShopMode.Viewing) {
      locationList.push(...locations.filter(f => f.service_browse));
    } else if (serviceMode === WebShopMode.Webvoucher) {
      locationList.push(...locations.filter(f => f.service_webvoucher));
    } else if (serviceMode === WebShopMode.Retail) {
      locationList.push(...locations.filter(f => f.service_retail));
    } else if (serviceMode === WebShopMode.Ticket) {
      locationList.push(...locations.filter(f => f.service_ticket));
    }
    return locationList;
  }

  /**
* Getting the list of locationDataModel
* Create locationViewModelList
* @result return a promise that resolves a list of locationViewModel
*/
  public GetWebshopLocationViewModel(serviceMode: number, webshopLocationList: number[], locationServiceStatus: LocationServiceStatus = LocationServiceStatus.IsNotActivated, coordinates: Coordinates = null): Promise<LocationWebshopViewModel[]> {
    const me = this;
    // return me.getLocationViewModel();
    return new Promise<LocationWebshopViewModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && webshopLocationList.includes(model.hyp_cus_nr))
        .then(async (locations) => {
          const locationList = me.filterLocationByServiceMode(locations, serviceMode);
          const now = moment();
          const currentTimeYMD = Utils.GetYMDFromMoment(now);
          const currentTimeHMS = Utils.GetHMSFromMoment(now);

          if (!coordinates && locationServiceStatus === LocationServiceStatus.Authorized) {
            coordinates = await me._deviceLocationService.getCurrentPosition();
          }
          me.mapDataModelListToWebshopViewModelList(locationList, currentTimeYMD, currentTimeHMS, serviceMode, coordinates)
            .then(resolve)
            .catch(e => {
              me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
              reject();
            });
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * get location list to check number of locations in CheckWebShopSingleLocationGuard
   * if there is one location, menu location for takeaway and webvoucher is not shwon
   * @param serviceMode current service mode
   * @param webshopLocationList list f location that participate in webshop
   */
  public GetWebshopGuardSingleLocationData(serviceMode: number, webshopLocationList: number[]): Promise<LocationDataModel[]> {
    const me = this;
    return new Promise<LocationDataModel[]>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && webshopLocationList.includes(model.hyp_cus_nr))
        .then((locations) => {
          const locationList = me.filterLocationByServiceMode(locations, serviceMode);
          resolve(locationList);
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetWebshopGuardLocationData", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**get webshop location info to use in webshop service
   * it return all the info for location such as open time, delivary area and other info that is used in webshop service
   * @servicemode webshop service type
   * @locationID customer id
   */
  public GetWebshopLocationInfoViewModel(serviceMode: WebShopMode, locationID: number): Promise<LocationWebshopViewModel> {
    const me = this;
    return new Promise<LocationWebshopViewModel>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate && model.hyp_cus_nr === locationID)
        .then((locations) => {
          if (locations.length === 1) {
            const now = moment();
            const currentTimeYMD = Utils.GetYMDFromMoment(now);
            const currentTimeHMS = Utils.GetHMSFromMoment(now);
            me.mapDataModelToWebshopViewModel(locations[0], currentTimeYMD, currentTimeHMS, serviceMode, null)
              .then(resolve)
              .catch(e => {
                me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
                reject();
              });
          } else {
            me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", "Could Not find valid Location");
            reject();
          }
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetLocationViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**get the webshop location Address to show in Basket
   * @locationID customer id
   */
  public GetWebshopLocationViewModelByID(locationID: number): Promise<BasketLocationViewModel> {
    const me = this;
    return new Promise<BasketLocationViewModel>((resolve, reject) => {
      // gets undeleted locations
      me._locationObsServ.GetObject(locationID)
        .then((location) => {
          resolve(me.mapLocationDataModelToWebShopViewModel(location));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetWebshopLocationViewModelByID", "GetObject", e);
          reject();
        });
    });
  }
  public GetWebshopCheckoutLocationViewModelByID(locationID: number): Promise<CheckoutLocationViewModel> {
    const me = this;
    return new Promise<CheckoutLocationViewModel>((resolve, reject) => {
      // gets undeleted locations
      me._locationObsServ.GetObject(locationID)
        .then((location) => {
          resolve(me.mapcheckoutLocationModelToWebShopViewModel(location));
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetWebshopCheckoutLocationViewModelByID", "GetObject", e);
          reject();
        });
    });
  }
  /**
   * get location list to check number of locations in CheckSingleLocationGuard
   * if there is one location, redirect to location detail
   */
  public GetSingleLocationData(): Promise<LocationDataModel> {
    const me = this;
    // return me.getLocationViewModel();
    return new Promise<LocationDataModel>((resolve, reject) => {
      me._locationObsServ.GetObjectsByFilter(model => !model.deleted && model.participate)
        .then((locations) => {
          if (locations && locations.length === 1) {
            resolve(locations[0]);
          } else {
            resolve(null);
          }
        })
        .catch(e => {
          me.objObsInjectHelper.coreLogService.error("LocationService.GetGuardSingleLocationData", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /** Fragt bei dem YourAppServer, welche Zeitspanen gerade nicht verfügbar sind
   * @param location Standort
   * @param order_type Bestellart (unterstützt nur Abholung und Lieferung)
   * @param timeslot_order_amount_type Muss dem in der Bestellart hinterlegtem entsprechen
   */
  public CheckTimeslotOrderAmount(location: number, order_type: orderTypes, timeslot_order_amount_type: number): Promise<CheckTimeslotOrderAmount> {
    const me = this;
    return new Promise<CheckTimeslotOrderAmount>((resolve, reject) => {
      try {
        const RequestData = {
          hyp_cus_nr: location,
          order_type: order_type,
          timeslot_order_amount_type: timeslot_order_amount_type
        };
        const FunctionName = "location/CheckTimeslotOrderAmount";
        me.objObsInjectHelper.yourAppServer.Api.RequestService(CheckTimeslotOrderAmount, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug("LocationService.CheckTimeslotOrderAmount", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me._coreLogService.error("LocationService.CheckTimeslotOrderAmount", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me._coreLogService.error("LocationService.CheckTimeslotOrderAmount", "RequestService", error); reject(); });
      } catch (error) {
        me._coreLogService.error("LocationService.CheckTimeslotOrderAmount", "try catch", error);
        reject();
      }
    });
  }

  // Mapper

  /**
  * map a list of locationDataModel to a list of locationViewModel
  * @param model Get a list of locationDataModel for mapping
  * @param currentTimeYMD used for setting openTime
  * @param currentTimeHMS used for setting openTime
  * @result return a list of locationViewModel
  */
  private mapDataModelListToViewModelList(modelList: LocationDataModel[], currentTimeYMD: number, currentTimeHMS, coordinates: Coordinates, hasClockLabel: boolean = true): Promise<LocationViewModel[]> {
    const me = this;
    if (modelList) {
      return Promise.all(modelList.filter(f => f.open_days && f.open_days.length > 0).map((model => me.mapDataModelToViewModel(model, currentTimeYMD, currentTimeHMS, coordinates, hasClockLabel))));
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   * map a locationDataModel to locationViewModel
   * @param model Get a locationDataModel for mapping
   * @param currentTimeYMD used for setting openTime
   * @param currentTimeHMS used for setting openTime
   * @result return a locationViewModel
   */
  private async mapDataModelToViewModel(model: LocationDataModel, currentTimeYMD: number, currentTimeHMS, coordinates: Coordinates, hasClockLabel: boolean = true): Promise<LocationViewModel> {
    const me = this;
    const viewModel = new LocationViewModel();
    viewModel.hyp_cus_nr = model.hyp_cus_nr;
    viewModel.participate = model.participate;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.street = model.street;
    viewModel.location = model.location;
    viewModel.zipCode = model.zip;
    viewModel.phone_no = model.phone_no;
    viewModel.mail = model.mail;
    viewModel.latitude = model.latitude;
    viewModel.longitude = model.longitude;
    viewModel.sort_nr = model.sort_nr;
    viewModel.serviceBrowse = model.service_browse;
    viewModel.serviceDelivery = model.service_delivery;
    // viewModel.serviceDeliveryGroup = model.service_delivery_group; // wird momentan nicht benötigt
    viewModel.serviceInhouse = model.service_inhouse;
    viewModel.serviceOrderahead = model.service_orderahead;
    viewModel.servicePickup = model.service_pickup;
    // viewModel.servicePickupGroup = model.service_pickup_group; // wird momentan nicht benötigt
    viewModel.serviceWebvoucher = model.service_webvoucher;
    viewModel.serviceRetail = model.service_retail;
    viewModel.serviceTicket = model.service_ticket;
    viewModel.image_photo = ContentType.Location + "/" + model.hyp_cus_nr + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    viewModel.distance = coordinates && model.longitude > 0 && model.latitude > 0 ? +(Utils.getDistanceBetweenPoints(coordinates.latitude, coordinates.longitude, model.latitude, model.longitude).toFixed(1)) : 1000000;
    const markYMD = model.GetTheoreticalOpenYMD(currentTimeYMD, currentTimeHMS);
    viewModel.openTime_de = model.GetOpenDayTimeAsString(CoreOpenDayTime["de"], markYMD, false, orderTypes.allUnknown, hasClockLabel);
    viewModel.openTime_en = model.GetOpenDayTimeAsString(CoreOpenDayTime["en"], markYMD, false, orderTypes.allUnknown, hasClockLabel);
    try {
      viewModel.like = await me._likeService.HasLike(ContentType.Location, model.hyp_cus_nr);
    } catch (error) {
      me._coreLogService.error("LocationService.mapDataModelToViewModel", "HasLike try catch", error);
    }
    viewModel.locationActions = me.setLocationActionList(model);
    try {
      const reservationSetting = await me._reservationSettingService.GetReservatonSetting();
      viewModel.hasReservation = reservationSetting && reservationSetting.includes(model.hyp_cus_nr);
      if (viewModel.hasReservation) {
        viewModel.locationActions.push(CustomAction.reservationAction);
      }
    } catch (error) {
      me._coreLogService.error("LocationService.mapDataModelToViewModel", "GetReservatonSetting try catch", error);
    }
    return Promise.resolve(viewModel);
  }

  private setLocationActionList(locationDetail: LocationDataModel): CustomAction[] {
    const actionList: CustomAction[] = [];
    if (locationDetail.phone_no !== "") {
      actionList.push(CustomAction.callAction);
    }
    if (!Utils.IsNull(locationDetail.latitude) && locationDetail.longitude) {
      actionList.push(CustomAction.directionAction);
    }
    if (locationDetail.mail !== "") {
      actionList.push(CustomAction.emailAction);
    }
    return actionList;
  }

  private mapModelToBonusLocationViewModel(model: LocationDataModel): BonusLocationViewModel {
    const viewModel: BonusLocationViewModel = new BonusLocationViewModel();
    viewModel.hyp_cus_nr = model.hyp_cus_nr;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.location = model.location;
    return viewModel;
  }

  private mapModelListToBonusLocationViewModelList(modelList: LocationDataModel[]): BonusLocationViewModel[] {
    const me = this;
    const result: BonusLocationViewModel[] = new Array();
    modelList.forEach(model => {
      result.push(me.mapModelToBonusLocationViewModel(model));
    });
    return result;
  }

  private mapModelToEventLocationViewModel(model: LocationDataModel): EventLocationViewModel {
    const viewModel: EventLocationViewModel = new EventLocationViewModel();
    viewModel.hyp_cus_nr = model.hyp_cus_nr;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    return viewModel;
  }

  private mapModelListToEventLocationViewModelList(modelList: LocationDataModel[]): EventLocationViewModel[] {
    const me = this;
    const result: EventLocationViewModel[] = new Array();
    modelList.forEach(model => {
      result.push(me.mapModelToEventLocationViewModel(model));
    });
    return result;
  }

  private mapModelToMenuLocationViewModel(model: LocationDataModel): MenuLocationViewModel {
    if (model) {
      const viewModel: MenuLocationViewModel = new MenuLocationViewModel();
      viewModel.hyp_cus_nr = model.hyp_cus_nr;
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.address = `${model.street ? model.street + ", " : ""}${model.zip ? model.zip + ", " : ""}${model.location ? model.location : ""}`;
      return viewModel;
    } else {
      return null;
    }
  }

  private mapModelToMenuChangeLocationViewModel(model: LocationDataModel, serviceID: number): MenuChangeLocationViewModel {
    if (model) {
      const now = moment();
      const currentTimeYMD = Utils.GetYMDFromMoment(now);
      const currentTimeHMS = Utils.GetHMSFromMoment(now);
      const markYMD = model.GetTheoreticalOpenYMD(currentTimeYMD, currentTimeHMS);
      const viewModel: MenuChangeLocationViewModel = new MenuChangeLocationViewModel();
      viewModel.hyp_cus_nr = model.hyp_cus_nr;
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.street = model.street;
      viewModel.zip = model.zip;
      viewModel.location = model.location;
      const markMoment = Utils.GetMomentFromInt(markYMD);
      const next7days = new Array(7).fill(0).map((_, i) => markMoment.clone().add(i, "day"));
      viewModel.openDays = next7days.map(day => new LocationOpenDayViewModel(model, day, serviceID));
      return viewModel;
    } else {
      return null;
    }
  }

  /**
* map a list of locationDataModel to a list of locationViewModel
* @param model Get a list of locationDataModel for mapping
* @param weekday used for setting openTime
* @result return a list of locationViewModel
*/
  private mapDataModelListToWebshopViewModelList(modelList: LocationDataModel[], currentTimeYMD: number, currentTimeHMS: number, serviceType: number, coordinates: Coordinates): Promise<LocationWebshopViewModel[]> {
    const me = this;
    if (modelList) {
      return Promise.all(modelList.filter(f => f.open_days && f.open_days.length > 0).map((model => me.mapDataModelToWebshopViewModel(model, currentTimeYMD, currentTimeHMS, serviceType, coordinates))));
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   * map a locationDataModel to locationViewModel
   * @param model Get a locationDataModel for mapping
   * @param currentTimeYMD used for setting openTime
   * @param currentTimeHMS used for setting openTime
   * @result return a locationViewModel
   */
  private async mapDataModelToWebshopViewModel(model: LocationDataModel, currentTimeYMD: number, currentTimeHMS: number, serviceType: number, coordinates: Coordinates): Promise<LocationWebshopViewModel> {
    // TODO serviceType muss WebShopMode werden, da es unnötig ist zwei enums für den selben inhalt zu halten
    const me = this;
    const viewModel = new LocationWebshopViewModel();
    viewModel.model = model;
    viewModel.distance = coordinates && model.longitude > 0 && model.latitude > 0 ? +(Utils.getDistanceBetweenPoints(coordinates.latitude, coordinates.longitude, model.latitude, model.longitude).toFixed(1)) : 1000000;

    // delivery group and pickup group are dependent on delivery and pickup and so their open status is the same
    const serviceTypeToUseForCheckingOpen = serviceType === orderTypes.deliveryGroup ? orderTypes.delivery
                          :  serviceType === orderTypes.pickupGroup ? orderTypes.pickup
                          :  serviceType;
    const markYMD = model.GetTheoreticalOpenYMD(currentTimeYMD, currentTimeHMS, serviceTypeToUseForCheckingOpen);
    const today = model.GetOpenDayForYMD(markYMD, serviceTypeToUseForCheckingOpen);
    viewModel.serviceType = serviceType;
    viewModel.open = today ? today.open : false;
    const markMoment = Utils.GetMomentFromInt(markYMD);
    const next7days = new Array(7).fill(0).map((_, i) => markMoment.clone().add(i, "day"));
    viewModel.openDays = next7days.map(day => new LocationOpenDayViewModel(model, day, serviceType));
    if (today && today.open) {
      viewModel.momentOpenTimeService = today.times;
    } else {
      viewModel.momentOpenTimeService = [];
    }
    try {
      viewModel.like = await me._likeService.HasLike(ContentType.Location, model.hyp_cus_nr);
    } catch (error) {
      me._coreLogService.error("LocationService.mapDataModelToViewModel", "HasLike try catch", error);
    }
    return Promise.resolve(viewModel);
  }
  public mapLocationDataModelToWebShopViewModel(model: LocationDataModel): BasketLocationViewModel {
    const me = this;
    if (model) {
      const viewModel = new BasketLocationViewModel();
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.location = model.location;
      viewModel.street = model.street;
      viewModel.phone_no = model.phone_no;
      viewModel.zip = model.zip;
      viewModel.impressum_html_de = model.impressum_html_de;
      viewModel.impressum_html_en = model.impressum_html_en;
      viewModel.stnr = model.stnr;
      viewModel.ustid = model.ustid;
      viewModel.commercial_register = model.commercial_register;
      viewModel.local_court = model.local_court;
      viewModel.representative = model.representative;
      return viewModel;
    } else {
      return null
    }
  }
  public mapCheckoutLocationDataModelToWebShopViewModel(model: LocationDataModel): CheckoutLocationViewModel {
    const me = this;
    if (model) {
      const viewModel = new CheckoutLocationViewModel();
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.location = model.location;
      viewModel.street = model.street;
      viewModel.phone_no = model.phone_no;
      viewModel.zip = model.zip;
      viewModel.impressum_html_de = model.impressum_html_de;
      viewModel.impressum_html_en = model.impressum_html_en;
      viewModel.stnr = model.stnr;
      viewModel.ustid = model.ustid;
      viewModel.commercial_register = model.commercial_register;
      viewModel.local_court = model.local_court;
      viewModel.representative = model.representative;
      return viewModel;
    } else {
      return null
    }
  }
  public mapcheckoutLocationModelToWebShopViewModel(model: LocationDataModel): CheckoutLocationViewModel {
    const me = this;
    if (model) {
      const viewModel = new CheckoutLocationViewModel();
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.location = model.location;
      viewModel.street = model.street;
      viewModel.zip = model.zip;
      viewModel.impressum_html_de = model.impressum_html_de;
      viewModel.impressum_html_en = model.impressum_html_en;
      viewModel.stnr = model.stnr;
      viewModel.ustid = model.ustid;
      viewModel.commercial_register = model.commercial_register;
      viewModel.local_court = model.local_court;
      viewModel.representative = model.representative;
      return viewModel;
    } else {
      return null
    }
  }
  public ngOnDestroy(): void {
    const me = this;
    //if (me._countrySupscription) {
    //  me._countrySupscription.unsubscribe();
    //}
    if (me._locationSupscription) {
      me._locationSupscription.unsubscribe();
    }
  }

}
